import React from 'react';
import { WidthProvider, Responsive } from 'react-grid-layout';
import { 
  Card, 
  Typography, 
  CardContent, 
  Box, 
  Divider,
  List,
  ListItem,
  ListItemText,
  Alert
} from '@mui/material';
import { Layout } from '../atoms-types/layout';
import EmailIcon from '@mui/icons-material/Email';
import SmsIcon from '@mui/icons-material/Sms';
import NotificationsIcon from '@mui/icons-material/Notifications';
import HelpIcon from '@mui/icons-material/Help';

const ResponsiveGridLayout = WidthProvider(Responsive);

const NotificationsHelp: React.FC = () => {

  const layout: Layout[] = [
    { i: 'a', x: 0, y: 0, w: 9, h: 20, minW: 9, minH: 8 },
  ];

  return (
    <ResponsiveGridLayout 
      className="layout" 
      layouts={{ lg: layout }} 
      breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480 }} 
      cols={{ lg: 9, md: 6, sm: 3, xs: 1 }}
      rowHeight={100}
      draggableCancel=".cancelSelectorName"
      isDraggable={false}
      isResizable={false}
      >
      <div key="a" style={{ minHeight: "800px" }} className='card-gauge'>
        <Card sx={{ display: 'flex', flexDirection: 'column', width: '100%', height: '100%' }}>
          <CardContent sx={{ p: 4, pb: 4, maxHeight: 'calc(100vh - 100px)', overflowY: 'auto', overflowX: 'hidden' }}>
            {/* Header */}
            <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
              <NotificationsIcon sx={{ fontSize: 40, color: 'primary.main' }} />
              <Typography variant="h3" sx={{ fontWeight: 'bold' }}>
                Notifications Help
              </Typography>
            </Box>

            <Typography variant="body1" sx={{ mb: 4, color: 'text.secondary' }}>
              Stay informed about important events and updates from your golf course monitoring system. 
              This guide will help you set up and manage your notification preferences.
            </Typography>

            <Divider sx={{ my: 3 }} />

            {/* Overview Section */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h4" sx={{ mb: 2, fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
                <HelpIcon sx={{ color: 'primary.main' }} />
                Overview
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                ODP.Golf notifications keep you informed about important events happening at your golf course. 
                You can receive notifications via:
              </Typography>
              <List sx={{ pl: 2 }}>
                <ListItem sx={{ pl: 0 }}>
                  <ListItemText 
                    primary="Email Notifications" 
                    secondary="Receive detailed notifications in your email inbox"
                  />
                </ListItem>
                <ListItem sx={{ pl: 0 }}>
                  <ListItemText 
                    primary="SMS/Text Notifications" 
                    secondary="Get instant alerts via text message to your phone"
                  />
                </ListItem>
              </List>
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                  Notification Channels:
                </Typography>
                <Typography variant="body2" component="div">
                  <ul style={{ margin: 0, paddingLeft: 20 }}>
                    <li>You can subscribe to both email and SMS notifications, or choose just one</li>
                    <li>Each channel requires separate verification</li>
                  </ul>
                </Typography>
              </Alert>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Email Notifications Section */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h4" sx={{ mb: 2, fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
                <EmailIcon sx={{ color: 'primary.main' }} />
                Email Notifications
              </Typography>
              
              <Typography variant="h6" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>
                Setting Up Email Notifications
              </Typography>
              <List sx={{ pl: 2 }}>
                <ListItem sx={{ pl: 0, display: 'list-item' }}>
                  <ListItemText 
                    primary="1. Enable Email Notifications"
                    secondary="Go to Account Settings and toggle on 'Email Notifications'"
                  />
                </ListItem>
                <ListItem sx={{ pl: 0, display: 'list-item' }}>
                  <ListItemText 
                    primary="2. Enter Your Email Address"
                    secondary="Click 'Add' or 'Change' to enter or update your email address"
                  />
                </ListItem>
                <ListItem sx={{ pl: 0, display: 'list-item' }}>
                  <ListItemText 
                    primary="3. Verify Your Email"
                    secondary="Check your inbox for a verification email and click the verification link"
                  />
                </ListItem>
                <ListItem sx={{ pl: 0, display: 'list-item' }}>
                  <ListItemText 
                    primary="4. Confirmation"
                    secondary="Once verified, you'll see 'Email verified ✓' and will start receiving notifications"
                  />
                </ListItem>
              </List>

              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                  💡 Tips:
                </Typography>
                <Typography variant="body2" component="div">
                  <ul style={{ margin: 0, paddingLeft: 20 }}>
                    <li>If you don't receive the verification email, check your spam folder</li>
                    <li>You can resend the verification email by clicking "Send verification email"</li>
                    <li>Your email address is saved even if you turn off notifications - just toggle it back on to resume</li>
                  </ul>
                </Typography>
              </Alert>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* SMS Notifications Section */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h4" sx={{ mb: 2, fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
                <SmsIcon sx={{ color: 'primary.main' }} />
                SMS/Text Notifications
              </Typography>
              
              <Typography variant="h6" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>
                Setting Up SMS Notifications
              </Typography>
              <List sx={{ pl: 2 }}>
                <ListItem sx={{ pl: 0, display: 'list-item' }}>
                  <ListItemText 
                    primary="1. Enable Text Message Notifications"
                    secondary="Go to Account Settings and toggle on 'Text Message Notifications'"
                  />
                </ListItem>
                <ListItem sx={{ pl: 0, display: 'list-item' }}>
                  <ListItemText 
                    primary="2. Enter Your Phone Number"
                    secondary="Click 'Add' or 'Change' to enter your 10-digit phone number (US format)"
                  />
                </ListItem>
                <ListItem sx={{ pl: 0, display: 'list-item' }}>
                  <ListItemText 
                    primary="3. Verify Your Phone"
                    secondary="You'll receive an opt-in text message. Reply with 'YES' to confirm your subscription"
                  />
                </ListItem>
                <ListItem sx={{ pl: 0, display: 'list-item' }}>
                  <ListItemText 
                    primary="4. Confirmation"
                    secondary="Once verified, you'll see 'Phone verified ✓' and will start receiving text notifications"
                  />
                </ListItem>
              </List>

              <Alert severity="warning" sx={{ mt: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                  Important SMS Information:
                </Typography>
                <Typography variant="body2" component="div">
                  <ul style={{ margin: 0, paddingLeft: 20 }}>
                    <li>Message and data rates may apply</li>
                    <li>Reply <strong>STOP</strong> to unsubscribe at any time</li>
                    <li>Reply <strong>HELP</strong> for assistance</li>
                    <li>If your number is on the STOP list, text <strong>START</strong> or <strong>UNSTOP</strong> to resubscribe</li>
                  </ul>
                </Typography>
              </Alert>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Notification Groups Section */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h4" sx={{ mb: 2, fontWeight: 'bold' }}>
                Notification Groups
              </Typography>
              
              <Typography variant="body1" sx={{ mb: 2 }}>
                Notification groups allow you to customize which types of events you receive notifications for. 
                You can subscribe to different groups for email and SMS separately.
              </Typography>

              <Typography variant="h6" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>
                Managing Notification Groups
              </Typography>
              <List sx={{ pl: 2 }}>
                <ListItem sx={{ pl: 0, display: 'list-item' }}>
                  <ListItemText 
                    primary="View Available Groups"
                    secondary="After enabling email or SMS notifications, you'll see available notification groups below"
                  />
                </ListItem>
                <ListItem sx={{ pl: 0, display: 'list-item' }}>
                  <ListItemText 
                    primary="Add a Group"
                    secondary="Click the '+' button to add a notification group. Select a group from the dropdown menu"
                  />
                </ListItem>
                <ListItem sx={{ pl: 0, display: 'list-item' }}>
                  <ListItemText 
                    primary="Remove a Group"
                    secondary="Click the 'X' button on any group chip to unsubscribe from that group"
                  />
                </ListItem>
                <ListItem sx={{ pl: 0, display: 'list-item' }}>
                  <ListItemText 
                    primary="Group Descriptions"
                    secondary="Each group shows a description of what types of notifications it includes"
                  />
                </ListItem>
              </List>

              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                  Note:
                </Typography>
                <Typography variant="body2" component="div">
                  <ul style={{ margin: 0, paddingLeft: 20 }}>
                    <li>You must have email or SMS notifications enabled and verified before you can add notification groups</li>
                  </ul>
                </Typography>
              </Alert>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Managing Subscriptions Section */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h4" sx={{ mb: 2, fontWeight: 'bold' }}>
                Managing Your Subscriptions
              </Typography>
              
              <Typography variant="h6" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>
                Unsubscribing
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                You can unsubscribe from notifications at any time:
              </Typography>
              <List sx={{ pl: 2 }}>
                <ListItem sx={{ pl: 0, display: 'list-item' }}>
                  <ListItemText 
                    primary="Via Account Settings"
                    secondary="Toggle off 'Email Notifications' or 'Text Message Notifications' in Account Settings"
                  />
                </ListItem>
                <ListItem sx={{ pl: 0, display: 'list-item' }}>
                  <ListItemText 
                    primary="Via SMS"
                    secondary="Reply 'STOP' to any text message to unsubscribe from SMS notifications"
                  />
                </ListItem>
              </List>

              <Typography variant="h6" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>
                Changing Your Contact Information
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                To update your email or phone number:
              </Typography>
              <List sx={{ pl: 2 }}>
                <ListItem sx={{ pl: 0, display: 'list-item' }}>
                  <ListItemText 
                    primary="Click 'Change' next to your current email or phone number"
                    secondary="Enter your new contact information and save. You'll need to verify the new address/number"
                  />
                </ListItem>
              </List>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Troubleshooting Section */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h4" sx={{ mb: 2, fontWeight: 'bold' }}>
                Troubleshooting
              </Typography>
              
              <Typography variant="h6" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>
                Email Issues
              </Typography>
              <List sx={{ pl: 2 }}>
                <ListItem sx={{ pl: 0, display: 'list-item' }}>
                  <ListItemText 
                    primary="Didn't receive verification email?"
                    secondary="Check your spam/junk folder. If it's not there, click 'Send verification email' to resend"
                  />
                </ListItem>
                <ListItem sx={{ pl: 0, display: 'list-item' }}>
                  <ListItemText 
                    primary="Verification link expired?"
                    secondary="Request a new verification email - the link will be fresh"
                  />
                </ListItem>
                <ListItem sx={{ pl: 0, display: 'list-item' }}>
                  <ListItemText 
                    primary="Not receiving notifications?"
                    secondary="Make sure your email is verified (shows 'Email verified ✓') and you're subscribed to at least one notification group"
                  />
                </ListItem>
              </List>

              <Typography variant="h6" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>
                SMS Issues
              </Typography>
              <List sx={{ pl: 2 }}>
                <ListItem sx={{ pl: 0, display: 'list-item' }}>
                  <ListItemText 
                    primary="Didn't receive verification text?"
                    secondary="Click 'Send verification text' to resend the opt-in message"
                  />
                </ListItem>
                <ListItem sx={{ pl: 0, display: 'list-item' }}>
                  <ListItemText 
                    primary="Phone number on STOP list?"
                    secondary="Text 'START' or 'UNSTOP' to the Twilio number (+16038369936) to resubscribe, then try again"
                  />
                </ListItem>
                <ListItem sx={{ pl: 0, display: 'list-item' }}>
                  <ListItemText 
                    primary="Not receiving texts?"
                    secondary="Make sure your phone is verified (shows 'Phone verified ✓') and you're subscribed to at least one notification group"
                  />
                </ListItem>
              </List>

              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                  Need more help?
                </Typography>
                <Typography variant="body2" component="div">
                  <ul style={{ margin: 0, paddingLeft: 20 }}>
                    <li>If you continue to experience issues, please contact support or check your Account Settings page to verify your notification preferences are correctly configured</li>
                  </ul>
                </Typography>
              </Alert>
            </Box>
          </CardContent>
        </Card>
      </div>
    </ResponsiveGridLayout>
  );
};

export default NotificationsHelp;